import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import { CheckoutForm } from '@app/components/stripe/checkout'
import {
  Button,
  LinearBottom,
  PaymentPlans,
  SnackBar,
} from '@app/components/general'
import { metaSetter } from '@app/utils'
import { EmptyPayments } from '@app/components/empty/views/payments'
import { StripProvider } from '@app/components/stripe/stripe-provider'
import { priceHandler } from '@app/utils/price-handler'
import { usePaymentsHook } from '@app/data/external/payments/use-payments'
import { roleMap } from '@app/utils/role-map'
import { useAuthContext } from '@app/components/providers/auth'
import { PriceTotal } from '@app/components/general/payments/total'
import { Header2, Header3 } from '@app/components/general/header'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { classNames } from '@app/utils/classes'
import { AuthContainer } from '@app/components/containers/auth-container'
import { BottomMenu } from '@app/components/general/navigation/bottom'
import { AuthRequired } from '@app/components/empty/auth-required'
import { getSubmitStyles } from '@app/components/stripe/submit-btn'
import { AppLayout } from '@app/components/app-layout'
import { Paragraph } from '@app/components/stateless/typo/paragraph'
import { FaqSection } from '@app/components/marketing/faq-section'
import { SwitchToggle } from '@app/components/general/switch-toggle'
import { useWebsiteContext } from '@app/components/providers/website'
import { rateMap } from '@app/utils/ssr/ratemap'
import { Appearance } from '@stripe/stripe-js'
import { useTheme } from '@app/components/themes/theme'
import loadNamespaces from 'next-translate/loadNamespaces'
import { I18nDictionary } from 'next-translate'
import { currencyConvert } from '@app/utils/ssr/currency-convert'
import type { GetStaticProps } from 'next'
import {
  determineSubmitLabel,
  renderPaymentTitle,
} from '@app/lib/payments/payments-view'
import { fetcher } from '@app/utils/fetcher'
import { Toaster } from '@app/@/components/ui/toaster'
import { useToast } from '@app/@/components/ui/use-toast'

type I18nProps = {
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
  // used to multiple the value from USD. Example 1.00 or 1 USD times the value 1 * 50 = 50. The price does not change.  Now when other currencies are set, use this based on how far or less from 1.00 baseline.
  __convertor?: number
}

export const getStaticProps: GetStaticProps<I18nProps> = async (ctx) => {
  const ns = (await loadNamespaces({
    ...ctx,
    pathname: '/payments',
  })) as I18nProps

  let data = null
  if (ns.__lang !== 'en') {
    data = await currencyConvert()
  }
  if (data && data.rates) {
    const mapper =
      ns.__lang in rateMap ? rateMap[ns.__lang as keyof typeof rateMap] : null

    if (mapper) {
      ns.__convertor = data.rates[mapper]
    }
  }
  return {
    props: ns,
    revalidate: 3600 * 12 * 2, // every 24 hours
  }
}

// move plan and yearset SSR
function Payments({ __convertor }: { __convertor?: number }) {
  const router = useRouter()
  const { t, lang } = useTranslation('payments')
  const { theme, systemTheme } = useTheme()
  const { data, loading, onToken } = usePaymentsHook()
  const { addWebsite } = useWebsiteContext()
  const { toast } = useToast()

  const [newCard, setNewCard] = useState<boolean>(false)
  const [yearly, setYearly] = useState<boolean>(true)
  const [selectedPlan, setState] = useState<string>('')
  const [addCredits, setCreditsView] = useState<boolean>(false)
  const [credits, setCredits] = useState<string>('2.5')
  const [clientSecret, setClientSecret] = useState('')
  const [ephemiral, setEphem] = useState()

  const mounted = useRef<boolean>(false)
  const mountedCredits = useRef<boolean>(false)

  const { account } = useAuthContext()

  // check for the theme to force dark mode.
  const appearance: Appearance = {
    theme:
      (systemTheme == 'dark' || theme === 'dark') && theme !== 'light'
        ? 'night'
        : 'stripe',
  }

  const options = {
    clientSecret,
    appearance,
  }

  // router plan query
  const queryPlan = (router?.query?.plan as string) ?? ''
  const yearSet = (router?.query?.yearly as string) ?? ''
  const creditsSet = router?.query?.credits
    ? Number(router.query.credits || 0)
    : ''

  // user data
  const role = data?.role || 0

  // HOBBY
  const hobbySet = (queryPlan && queryPlan === 'C1') || role === 11
  const paymentSubscription = data?.paymentSubscription
  // allow payments on all non maxed accounts
  const paymentIsYearly = data?.paymentSubscription?.plan?.interval === 'year'
  const priceMultiplyier = yearly ? 0 : ''

  // default to active plan
  useEffect(() => {
    // set if yearly
    if (role) {
      setYearly(paymentIsYearly)
    }
  }, [paymentIsYearly, setYearly, role])

  // use current plan to detect user
  let currentPlan = roleMap(role)

  const basePlan = role && currentPlan

  const price = priceHandler(selectedPlan || basePlan || 'L1')

  // todo: parse int instead
  const selectedPrice = addCredits
    ? Number(credits ? Number(credits || 0) * 100 : 0)
    : Number(`${price}${priceMultiplyier}`)

  // ACTIVE PAYMENT PLAN
  const paymentPlan = selectedPlan || basePlan || 'L1'

  // TODO: reset the index on tab change
  const initialSelectIndex = useMemo(() => {
    let idx = 0

    if (queryPlan && queryPlan.length === 2) {
      const nm = parseInt(queryPlan[1])

      if (!Number.isNaN(nm) && nm > 0 && nm < 11) {
        idx = nm - 1
      }
    } else {
      if (role > 5) {
        idx = role - 6
      } else if (role) {
        idx = role - 1
      }
    }

    return idx
  }, [queryPlan, role])

  const stripeID = data?.stripeID

  const paymentActiveConfig = useMemo(() => {
    const creditsPassed = addCredits && credits ? credits : undefined
    const _credits = creditsPassed ? Number(creditsPassed) : undefined
    const validCredits =
      creditsPassed && _credits && !isNaN(_credits) ? _credits : undefined
    const _validCredits =
      validCredits && validCredits > 0 ? validCredits : undefined

    return {
      plan: paymentPlan,
      yearly,
      credits: _validCredits,
      stripeID,
    }
  }, [paymentPlan, yearly, credits, addCredits, stripeID])

  useEffect(() => {
    // Set the initial mounted state
    if (!mounted.current) {
      setYearly(!!yearSet)

      if (queryPlan) {
        setState(queryPlan)
      }
      mounted.current = true
    }
    if (creditsSet && !mountedCredits.current) {
      setCredits(creditsSet + '')
      setCreditsView(true)
      mountedCredits.current = true
    }
  }, [
    yearSet,
    queryPlan,
    mounted,
    creditsSet,
    setCreditsView,
    setCredits,
    mountedCredits,
  ])

  const fetchPaymentSubscription = useCallback(async () => {
    // if stripeID is not found fetch it from the db
    const { data } = await fetcher(
      '/create-payment-subscription',
      {
        customer: paymentActiveConfig?.stripeID,
        metadata: paymentActiveConfig,
        plan: paymentPlan,
        yearly,
      },
      'POST'
    )

    if (data) {
      setClientSecret(data?.clientSecret)
      if (data?.ephemeralKey) {
        setEphem(data.ephemeralKey)
      }
    }
  }, [paymentActiveConfig, paymentPlan, yearly])

  // fetch a payment intent from the server for credits purchase.
  const fetchPaymentIntent = useCallback(async () => {
    const { data } = await fetcher(
      '/create-payment-intent-credits',
      {
        customer: paymentActiveConfig?.stripeID,
        metadata: paymentActiveConfig,
        credits,
      },
      'POST'
    )
    setClientSecret(data?.clientSecret)
    // setEphem(data?.ephemeralKey || undefined)
  }, [paymentActiveConfig, credits])

  // RESET VARS FOR THE UI: on new year change
  if (yearly && !paymentIsYearly) {
    currentPlan = ''
  } else if (!yearly && paymentIsYearly) {
    currentPlan = ''
  }

  // not 11
  const userHasHighPlan = role > 5 && ![11, 20].includes(role)
  // Determine if plans can be submitted
  const submitPlanCapable =
    selectedPlan && (selectedPlan !== basePlan || paymentIsYearly !== yearly)
  const downgradePlan = determineSubmitLabel(currentPlan, selectedPlan)
  const defaultPaymentOption =
    data && data.paymentOptions?.length ? data.paymentOptions[0] : null
  const cardFound = defaultPaymentOption ?? data?.paymentSubscription
  const creditsSubmit =
    addCredits && credits && !isNaN(Number(credits)) && Number(credits) > 0

  const checkoutFormVisible = !cardFound || newCard

  useEffect(() => {
    // wait until the loading is finished. TODO: perform Server side validation instead.
    if (loading) {
      return
    }
    // fetch and reflect the updated payment intent on the screen as needed. - We may need to move event changes to
    // direct hook events to prevent lag in between.
    // we would only fire the initial request on mount.
    // so removing the side effects can be done when the purchase btn is done afterwards to get the new intent
    // instead of multiple calls each time.

    if (checkoutFormVisible && account.inited && mounted.current) {
      // TODO: validate payment intent on the clientside without extra server fetching.
      if (addCredits) {
        fetchPaymentIntent()
      } else {
        fetchPaymentSubscription()
      }
    }
  }, [
    mounted,
    account.inited,
    addCredits,
    paymentActiveConfig,
    checkoutFormVisible,
    loading,
    fetchPaymentIntent,
    fetchPaymentSubscription,
    yearly,
  ])

  // on valid payment handling re-set current token
  const onTokenEvent = useCallback(
    async (token: string) => {
      const plans = await onToken(token, {
        plan: paymentActiveConfig.plan,
        yearly: paymentActiveConfig.yearly,
        credits: paymentActiveConfig.credits,
      })

      if (plans) {
        await router.push('/dashboard')
      }
    },
    [onToken, router, paymentActiveConfig]
  )

  // update to the next plan
  const handleChange = useCallback(
    (nextPlan: string) => {
      setState(nextPlan)
    },
    [setState]
  )

  const onToggleCredits = useCallback(async () => {
    try {
      await fetchPaymentIntent()
    } catch (e) {
      console.error(e)
      if (e instanceof Error) {
        toast({
          title: e.message,
        })
      }
    }
    setCreditsView((x) => !x)
  }, [setCreditsView, fetchPaymentIntent, toast])

  const submitPlan = useCallback(async () => {
    const creditsPassed = addCredits && credits ? credits : undefined
    const _credits = creditsPassed ? Number(creditsPassed) : undefined
    const validCredits =
      creditsPassed && _credits && !isNaN(_credits) ? _credits : undefined
    const _validCredits =
      validCredits && validCredits > 0 ? validCredits : undefined

    // TODO: error for 0 credits
    if (selectedPlan === 'enterprise') {
      return
    }

    const passed = await onToken('', {
      plan: selectedPlan ?? paymentPlan,
      yearly,
      credits: _validCredits,
    })

    if (!passed) {
      return
    }

    if (creditsPassed) {
      toast({
        title: t(_validCredits ? 'credits-applied' : 'errors:general-error')
          .replace('<0>', '')
          .replace('</0>', ''),
      })
    } else {
      toast({
        // todo: translate one string.
        title: `${t('price:active-p')}: ${selectedPlan}`,
      })
    }

    if (typeof sessionStorage !== 'undefined') {
      const websitesForm = sessionStorage.getItem('@app/subscribe-website')

      try {
        if (websitesForm) {
          const formValues = JSON.parse(websitesForm)
          await addWebsite({
            variables: formValues,
          })
          sessionStorage.removeItem('@app/subscribe-website')
        }
      } catch (e) {
        console.error(e)
      }
    }

    await router.push('/dashboard')
  }, [
    addWebsite,
    selectedPlan,
    paymentPlan,
    yearly,
    onToken,
    // refetch,
    t,
    addCredits,
    credits,
    router,
    toast,
  ])

  const onCreditsChange = (e: SyntheticEvent<HTMLInputElement>) =>
    setCredits(e.currentTarget.value)

  const onToggleCard = useCallback(() => setNewCard((x) => !x), [setNewCard])

  return (
    <AuthContainer
      translation='payments'
      pageTitle='menu:payments'
      pageSubtitle={renderPaymentTitle(
        !loading && role && !paymentSubscription
      )}
      smallContainer
      user={account}
    >
      {!data && !loading ? (
        <AuthRequired />
      ) : (
        <>
          <div className='sr-only'>
            <Header2>{t('header')}</Header2>
            <Paragraph>{t('header-sub')}</Paragraph>
          </div>
          <div className='py-6'>
            {loading && !data ? (
              <EmptyPayments />
            ) : (
              <>
                <div className='pb-5'>
                  <div className='flex space-x-2'>
                    <SwitchToggle
                      id={'add-credits'}
                      title={t(
                        !addCredits
                          ? 'credits-form:add-credits'
                          : 'credits-form:hide-credits'
                      )}
                      checked={addCredits}
                      onChange={onToggleCredits}
                      titleLabel
                    />
                  </div>
                </div>
                {addCredits ? (
                  <div className='space-y-3'>
                    <Header3>{t('credits-form:add-credits')}</Header3>
                    <Paragraph>{t('credits-form:credits-d')}</Paragraph>
                    <div className='py-2'>
                      <label className='sr-only'>
                        {t('credits-form:add-credits')}
                      </label>
                      <input
                        type={'number'}
                        onChange={onCreditsChange}
                        className='border dark:border-gray-700 rounded py-3 px-4'
                        value={credits}
                        min={2.5}
                        placeholder='2.5'
                      />
                      <div className='py-4'>
                        <Paragraph className='text-sm'>
                          {t('credits-form:estimated', {
                            value: `${Number(credits || 0) * 500} - ${
                              Number(credits || 0) * 1000
                            }`,
                            creditValue: credits,
                          })}
                        </Paragraph>
                      </div>
                    </div>
                  </div>
                ) : (
                  <PaymentPlans
                    onClick={handleChange}
                    setYearly={setYearly}
                    currentPlan={currentPlan}
                    initialIndex={initialSelectIndex}
                    highPlan={userHasHighPlan}
                    yearly={yearly}
                    selectIndex={hobbySet ? 0 : undefined}
                    __convertor={__convertor}
                  />
                )}

                {selectedPlan === 'enterprise' ? null : (
                  <div className='space-y-6 pt-2 pb-6'>
                    <div className='sm:w-full place-content-center place-items-center align-center md:min-w-[350px]'>
                      <div className='space-y-3'>
                        {checkoutFormVisible ? (
                          <>
                            {newCard && cardFound ? (
                              <Button
                                onClick={onToggleCard}
                                className={classNames(
                                  'text-sm border font-semibold text-yellow-500 border-yellow-600 dark:border-yellow-400 px-8 rounded-md',
                                  'hover:text-yellow-600 hover:border-yellow-600 dark:hover:border-yellow-600 min-w-[9rem]'
                                )}
                              >
                                {t('old-card')}
                              </Button>
                            ) : null}
                            {clientSecret ? (
                              <StripProvider options={options}>
                                <CheckoutForm
                                  onToken={onTokenEvent}
                                  plan={paymentPlan}
                                  price={selectedPrice}
                                  disabled={!paymentPlan}
                                  downgrade={downgradePlan}
                                  addCredits={addCredits}
                                  credits={Number(credits || 0)}
                                  ephemiral={ephemiral}
                                  __convertor={__convertor}
                                />
                              </StripProvider>
                            ) : null}
                          </>
                        ) : (
                          <div className='space-y-3'>
                            <>
                              <div className='pb-3 border-b dark:border-gray-800'>
                                <div className='flex gap-3'>
                                  {defaultPaymentOption ? (
                                    <>
                                      <div className='dark:bg-gray-800 dark:text-white border dark:border-gray-700 px-3 py-1 rounded'>
                                        <div className='dark:text-gray-300 text-xs'>
                                          {defaultPaymentOption.brand}
                                        </div>
                                        <div className='flex gap-1'>
                                          {defaultPaymentOption.last4 ? (
                                            <div className='text-xs'>
                                              ***{defaultPaymentOption.last4}
                                            </div>
                                          ) : null}
                                          {defaultPaymentOption.exp_month ? (
                                            <div className='text-xs font-light'>
                                              - {defaultPaymentOption.exp_month}{' '}
                                              / {defaultPaymentOption.exp_year}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    </>
                                  ) : null}
                                  <Button
                                    onClick={onToggleCard}
                                    className={classNames(
                                      'text-sm border font-semibold text-indigo-500 border-indigo-600 dark:border-indigo-400 px-8 rounded-md',
                                      'hover:text-blue-600 hover:border-blue-600 dark:hover:border-blue-600 min-w-[9rem]'
                                    )}
                                  >
                                    {t('new-card')}
                                  </Button>
                                </div>
                              </div>

                              <Button
                                type='button'
                                onClick={submitPlan}
                                border={false}
                                round={false}
                                disabled={
                                  creditsSubmit ? false : !submitPlanCapable
                                }
                                className={classNames(
                                  getSubmitStyles(
                                    creditsSubmit ? true : !!submitPlanCapable
                                  ),
                                  'flex gap-2 place-items-center place-content-center'
                                )}
                              >
                                {t(
                                  addCredits
                                    ? 'purchase'
                                    : downgradePlan
                                      ? 'downgrade'
                                      : 'upgrade'
                                )}{' '}
                                <PriceTotal
                                  price={
                                    __convertor
                                      ? selectedPrice * __convertor
                                      : selectedPrice
                                  }
                                  lang={
                                    __convertor && __convertor !== 1
                                      ? lang
                                      : undefined
                                  }
                                  noui
                                />
                              </Button>
                            </>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className='py-6'>
                  <div className='p-4'>
                    <abbr
                      className='text-center text-3xl font-bold no-underline block pb-2'
                      title={t('faq:header').replace(' (FAQ)', '')}
                    >
                      FAQs
                    </abbr>
                    <div className='border-t border-dotted dark:border-gray-700'>
                      <FaqSection
                        t={t}
                        lang={lang}
                        headers={'h4'}
                        blockIndex={[1, 5]}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <BottomMenu active='payments' />
        </>
      )}
      <LinearBottom loading={loading} />
      <SnackBar topLevel />
      <Toaster />
    </AuthContainer>
  )
}

Payments.getLayout = AppLayout

export default metaSetter(
  { Payments },
  {
    gql: true,
    rest: true,
    translation: 'payments',
  }
)
